import { Box, Collapse, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { StandardMeta, gradeLabel, gradeOrder } from '../providers/StandardsProvider'

interface Props {
  standardIds: string[]
  findStandard: (code: string) => StandardMeta | null
  subjectColor: string
  subjectBg: string
  collapsible?: boolean
  defaultOpen?: boolean
}

function PctRow({ label, count, pct, color }: { label: string; count: number; pct: number; color: string }) {
  return (
    <Box sx={{ mb: 0.75 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Typography variant="caption" sx={{ fontSize: 11, color: 'text.primary' }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
          {count} ({pct}%)
        </Typography>
      </Box>
      <Box sx={{ height: 4, bgcolor: 'grey.100', borderRadius: 1, overflow: 'hidden' }}>
        <Box
          sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 1, transition: 'width 0.4s ease' }}
        />
      </Box>
    </Box>
  )
}

export function DatasetCharacterizationPanel({
  standardIds,
  findStandard,
  subjectColor,
  subjectBg,
  collapsible = false,
  defaultOpen = true,
}: Props) {
  const [open, setOpen] = useState(defaultOpen)
  const [showAllDomains, setShowAllDomains] = useState(false)

  const stats = useMemo(() => {
    if (!standardIds.length) return null

    const metas: StandardMeta[] = []
    for (const id of standardIds) {
      const m = findStandard(id)
      if (m) metas.push(m)
    }
    if (!metas.length) return null

    // By grade
    const gradeCount: Record<string, number> = {}
    for (const m of metas) gradeCount[m.grade] = (gradeCount[m.grade] || 0) + 1
    const gradeRows = Object.entries(gradeCount)
      .sort((a, b) => gradeOrder(a[0]) - gradeOrder(b[0]))
      .map(([grade, count]) => ({ grade, count, pct: Math.round((count / metas.length) * 100) }))

    // By domain
    const domainCount: Record<string, number> = {}
    for (const m of metas) {
      const d = m.domain || 'Unknown'
      domainCount[d] = (domainCount[d] || 0) + 1
    }
    const domainRows = Object.entries(domainCount)
      .sort((a, b) => b[1] - a[1])
      .map(([domain, count]) => ({ domain, count, pct: Math.round((count / metas.length) * 100) }))

    // Sub-parts
    const subpartCount = metas.filter((m) => /\b[abc]\./.test(m.std.text)).length

    // Scope
    const uniqueGrades = gradeRows.map((r) => r.grade)
    const gradeSpan =
      uniqueGrades.length === 1
        ? `${gradeLabel(uniqueGrades[0])} only`
        : `${gradeLabel(uniqueGrades[0])} – ${gradeLabel(uniqueGrades[uniqueGrades.length - 1])}`
    const domainSpan = domainRows.length

    let characterization: 'Focused' | 'Moderate' | 'Broad'
    if (uniqueGrades.length >= 3 || domainSpan >= 5) characterization = 'Broad'
    else if (uniqueGrades.length === 1 && domainSpan <= 2) characterization = 'Focused'
    else characterization = 'Moderate'

    const charColor =
      characterization === 'Focused' ? '#16a34a' : characterization === 'Moderate' ? '#d97706' : '#dc2626'

    return {
      total: metas.length,
      gradeRows,
      domainRows,
      subpartCount,
      gradeSpan,
      domainSpan,
      characterization,
      charColor,
    }
  }, [standardIds.join(',')]) // eslint-disable-line

  if (!stats) return null

  const cardSx = {
    bgcolor: 'background.paper',
    borderRadius: 1.5,
    border: '1px solid',
    borderColor: 'divider',
    overflow: 'hidden',
  }

  const content = (
    <Box sx={{ p: 1.75, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      {/* Scope badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            borderRadius: 1.5,
            bgcolor: stats.charColor + '18',
            border: `1px solid ${stats.charColor}40`,
          }}
        >
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: stats.charColor, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 700, color: stats.charColor }}>
            {stats.characterization}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
          {stats.gradeSpan} · {stats.domainSpan} domain{stats.domainSpan !== 1 ? 's' : ''}
          {stats.subpartCount > 0 && ` · ${stats.subpartCount} with sub-parts`}
        </Typography>
      </Box>

      {/* Grade distribution — only when multiple grades */}
      {stats.gradeRows.length > 1 && (
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              fontSize: 10,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              display: 'block',
              mb: 0.875,
            }}
          >
            By Grade
          </Typography>
          {stats.gradeRows.map(({ grade, count, pct }) => (
            <PctRow key={grade} label={gradeLabel(grade)} count={count} pct={pct} color={subjectColor} />
          ))}
        </Box>
      )}

      {/* Domain distribution */}
      <Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: 'text.secondary',
            fontSize: 10,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            display: 'block',
            mb: 0.875,
          }}
        >
          By Domain
        </Typography>
        {(showAllDomains ? stats.domainRows : stats.domainRows.slice(0, 5)).map(({ domain, count, pct }) => (
          <PctRow key={domain} label={domain} count={count} pct={pct} color={subjectColor + 'aa'} />
        ))}
        {!showAllDomains && stats.domainRows.length > 5 && (
          <Typography
            onClick={() => setShowAllDomains(true)}
            variant="caption"
            sx={{ fontSize: 10.5, color: subjectColor, cursor: 'pointer', fontWeight: 600 }}
          >
            and {stats.domainRows.length - 5} more…
          </Typography>
        )}
      </Box>
    </Box>
  )

  if (!collapsible) {
    return <Box sx={cardSx}>{content}</Box>
  }

  return (
    <Box sx={cardSx}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          px: 1.75,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          bgcolor: open ? subjectBg : 'background.paper',
          borderBottom: open ? '1px solid' : 'none',
          borderColor: 'divider',
          transition: 'background-color 0.1s',
          '&:hover': { bgcolor: subjectBg },
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: subjectColor, fontSize: 12 }}>
          Dataset · {stats.total} standard{stats.total !== 1 ? 's' : ''}
        </Typography>
        <Typography sx={{ fontSize: 14, color: subjectColor, lineHeight: 1 }}>{open ? '▲' : '▼'}</Typography>
      </Box>
      <Collapse in={open}>{content}</Collapse>
    </Box>
  )
}
